﻿import "./App.scss";

import { BannerNotifications } from "@sbb-rsu/ui-banners";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AppProviders } from "AppProviders";
import { AppRoutes } from "AppRoutes";
import LazyBoard from "Board/LazyBoard";
import LazyBoardsAll from "Boards/LazyBoardsAll";
import LazyBoardsMy from "Boards/LazyBoardsMy";
import LazyBoardSettings from "BoardSettings/LazyBoardSettings";
import { Footer } from "components/Footer/Footer";
import { FixTooltips } from "components/GlobalTooltip/FixTooltips";
import { GlobalTooltip } from "components/GlobalTooltip/GlobalTooltip";
import { InlineSpinner } from "components/InlineSpinner";
import { NotFound } from "components/NotFound/NotFound";
import { useTranslations } from "providers";
import React from "react";
import Modal from "react-modal";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { ToastContainer } from "react-toastify";
import LazyRun from "Run/LazyRun";
import LazyRunRedirect from "Voting/LazyRunRedirect";
import LazyVoteRedirect from "Voting/LazyVoteRedirect";
import LazyVotingHome from "Voting/LazyVotingHome";
import LazyVotingView from "Voting/LazyVotingView";

import { Header } from "./components/Header/Header";

Modal.setAppElement("#root");

const queryClient = new QueryClient();

const RouteLoader: React.FC = () => (
  <div className="route-loading d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
    <InlineSpinner />
  </div>
);

export const App = () => {
  const i18n = useTranslations();
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AppProviders>
          <ReactQueryDevtools initialIsOpen={false} />
          <GlobalTooltip />
          <FixTooltips />
          <ToastContainer hideProgressBar theme="colored" pauseOnFocusLoss={true} position="top-right" />
          <Header />

          <BannerNotifications language={i18n.currentLanguage} className="rsu-banners" />

          <main role="main" className="main-content lean-section">
            <div className="container-fluid mb-5">
              <React.Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path={AppRoutes.Home} element={<Navigate to={AppRoutes.VotingOpen} />} />
                  <Route path={AppRoutes.Voting} element={<Navigate to={AppRoutes.VotingOpen} />} />
                  <Route path={AppRoutes.VotingOpen} element={<LazyVotingHome type="open" />} />
                  <Route path={AppRoutes.VotingDone} element={<LazyVotingHome type="done" />} />
                  <Route path={AppRoutes.VotingDetail} element={<LazyVotingView />} />

                  <Route path={AppRoutes.Boards} element={<Navigate to={AppRoutes.BoardsMy} />} />
                  <Route path={AppRoutes.BoardsMy} element={<LazyBoardsMy />} />
                  <Route path={AppRoutes.BoardsAll} element={<LazyBoardsAll />} />

                  <Route path={AppRoutes.Board} element={<LazyBoard />} />
                  <Route path={AppRoutes.BoardSettings} element={<LazyBoardSettings />} />
                  <Route path={AppRoutes.Run} element={<LazyRun />} />

                  <Route path={AppRoutes.VoteRedirect} element={<LazyVoteRedirect />} />
                  <Route path={AppRoutes.RunRedirect} element={<LazyRunRedirect />} />

                  <Route path={AppRoutes.NotFound} element={<NotFound />} />
                </Routes>
              </React.Suspense>
            </div>
          </main>
          <Footer />
        </AppProviders>
      </QueryClientProvider>
    </BrowserRouter>
  );
};
